import { fromPairs, keyBy } from 'lodash';
import { difference, union } from 'ramda';
import { CompanyId } from 'api/models';
import { Company as ApiCompany } from 'api/models/core-types';
import {
  Empty,
  Loaded,
  Loading,
  RefreshableContent,
  Refreshing,
} from 'stores/core';

export type Company = Omit<ApiCompany, 'companyLogo'>;

export type CompaniesStore = {
  ids: RefreshableContent<number[]>;
  byId: Record<number, Company>;
};

const EMPTY: CompaniesStore = {
  byId: {},
  ids: Empty(),
};

const load =
  () =>
  ({ byId, ids }: CompaniesStore): CompaniesStore => ({
    byId,
    ids:
      ids[0] === 'refreshing' || ids[0] === 'loading'
        ? ids
        : ids[0] === 'loaded'
        ? Refreshing(ids)
        : Loading(),
  });

const putAll = (companies: Company[]) => (): CompaniesStore => {
  const ids = companies.map(({ companyId }) => companyId);
  const lookup = keyBy(companies, 'companyId') as Record<number, Company>;

  return {
    byId: fromPairs(ids.map((id) => [id, lookup[id]])),
    ids: Loaded(ids),
  };
};

const loaded = ({ ids }: CompaniesStore) => ids[0] === 'loaded';

const toList = ({ ids: [, companyIds = []], byId }: CompaniesStore) =>
  companyIds.map((x) => byId[x]).filter(Boolean);

const ofId =
  ([, companyId]: CompanyId) =>
  ({ byId }: CompaniesStore) =>
    byId[companyId];

const added =
  (company: Company) =>
  (store: CompaniesStore): CompaniesStore => {
    const { byId, ids } = store;

    if (ids[0] !== 'loaded') {
      return store;
    }

    return {
      byId: { ...byId, [company.companyId]: company },
      ids: Loaded(union(ids[1], [company.companyId])),
    };
  };

const updated =
  (company: Company) =>
  (store: CompaniesStore): CompaniesStore =>
    added(company)(store);

const removed =
  ([, companyId]: CompanyId) =>
  (store: CompaniesStore): CompaniesStore => {
    const {
      byId: { [companyId]: _removed, ...byId },
      ids,
    } = store;

    if (ids[0] !== 'loaded') {
      return store;
    }

    return {
      byId,
      ids: Loaded(difference(ids[1], [companyId])),
    };
  };

export const CompaniesStore = {
  empty: () => EMPTY,
  load,
  putAll,
  loaded,
  toList,
  ofId,
  added,
  updated,
  removed,
};
